import { useMutation, useQuery } from "@blitzjs/rpc"
import { useEffect, useMemo, useRef, useState } from "react"
import Grid from "@mui/material/Grid"
import Card from "@mui/material/Card"
import Button from "@mui/material/Button"

import updateUser from "app/users/mutations/updateUser"
import { useCurrentUser } from "app/core/hooks/useCurrentUser"
import OptionFilters from "./OptionFilters"
import isUserSearchParamsEmpty from "./isUserSearchParamsEmpty"
import buildSearchQuery from "./buildSearchQuery"
import { Box } from "@mui/material"
import PipelineSearchFilter from "./PipelineSearchFilter"
import getAvailableFilterOptions from "app/pipelines/queries/getAvailableFilterOptions"
import AdvancedFilters from "./AdvancedFilters"
import { every, isEmpty, isUndefined } from "lodash"

const DEFAULT_SEARCH_PARAMS = {
  option: {
    brand: undefined,
    geography: undefined,
    language: undefined,
    githubRepositoryUrl: undefined,
    status: undefined,
    series: undefined,
  },
  text: undefined,
}

export default function PipelineFilters({ search, onSearch }) {
  const [updateUserMutation] = useMutation(updateUser)
  const [activeFilterOptions] = useQuery(getAvailableFilterOptions, search, { staleTime: Infinity })

  const searchParamsRef = useRef<any>(null)
  const currentUser = useCurrentUser()
  const currentUserStr = useMemo(() => {
    return JSON.stringify(currentUser)
  }, [currentUser])

  const [filters, setFilters] = useState({ ...DEFAULT_SEARCH_PARAMS })
  useEffect(() => {
    if (!currentUser) {
      return
    }

    const searchParams = isUserSearchParamsEmpty(currentUser)
      ? DEFAULT_SEARCH_PARAMS
      : (currentUser.pipelinesSearchParams as typeof DEFAULT_SEARCH_PARAMS)

    if (isEmpty(searchParams.option) || every(searchParams.option, isUndefined)) {
      searchParams.option.status = [{ code: "active", name: "Active" }] as any
    }

    updateFilters(searchParams)

    const updateUserSearchParams = async () => {
      if (searchParamsRef.current == currentUser!.pipelinesSearchParams) return

      await updateUserMutation({
        id: currentUser!.id,
        pipelinesSearchParams: searchParamsRef.current,
      })
    }

    window.addEventListener("beforeunload", updateUserSearchParams)

    return () => {
      window.removeEventListener("beforeunload", updateUserSearchParams)
      updateUserSearchParams().catch(console.error)
    }
  }, [currentUserStr])

  function updateOptionFilters(optionFiltersUpdate) {
    updateFilters({
      ...filters,
      option: optionFiltersUpdate,
    })
  }

  function updateFilters(filterUpdate) {
    searchParamsRef.current = filterUpdate
    setFilters(filterUpdate)
    callPipelinesSearch(filterUpdate)
  }

  function callPipelinesSearch(searchParams) {
    const searchQuery = buildSearchQuery(searchParams)
    onSearch((prevState) => Object.assign(prevState, searchQuery))
  }

  async function clearSearch() {
    const searchParams = { ...DEFAULT_SEARCH_PARAMS }
    searchParams.option.status = [{ code: "active", name: "Active" }] as any

    updateFilters(searchParams)
  }

  function updateSearchFilter(searchUpdate) {
    updateFilters({
      ...filters,
      text: searchUpdate,
    })
  }

  return (
    <Card sx={{ mb: 4, p: 2 }}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box
            display="grid"
            sx={{
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
                xl: "repeat(5, 1fr)",
              },
            }}
            gap={2}
            alignItems="center"
          >
            <OptionFilters
              filters={filters.option}
              onChange={updateOptionFilters}
              activeFilterOptions={activeFilterOptions}
            />
            <Box
              sx={{
                gridColumn: {
                  xs: "span 1",
                  sm: "span 2",
                },
              }}
            >
              <PipelineSearchFilter
                placeholder="Search by ID or Branch, Pipeline Name"
                value={filters.text}
                onChange={updateSearchFilter}
              />
            </Box>
            <AdvancedFilters
              filters={filters.option}
              onChange={updateOptionFilters}
              activeFilterOptions={activeFilterOptions}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid item xs={12} md={12}>
        <Box display="flex" justifyContent="end" alignItems="center" gap={2}>
          <Button variant="text" color="secondary" onClick={clearSearch}>
            Clear
          </Button>
        </Box>
      </Grid>
    </Card>
  )
}
